import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { ShieldCheck, UserPlus, Users, Sparkles } from 'lucide-react';

import { PageHeader } from '../components/PageHeader';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectItem } from '../components/ui/select';
import { canManageUsers, getUserRole } from '../lib/access';
import axiosInstance from '../../axiosInstance';
import configData from '../../config';

const initialForm = {
  memberName: '',
  email: '',
  password: '',
  phone: '',
  role: '',
};

const api = (path) => `${String(configData.API_SERVER || '').replace(/\/+$/, '')}${path}`;
const defaultRoles = [
  { role: 'Super Admin' },
  { role: 'Admin' },
  { role: 'Member' },
];

export function UserAllocationPage() {
  const account = useSelector((state) => state.account || {});
  const orgId = useSelector((state) =>
    state?.account?.org?.id ||
    state?.account?.org?.data?.id ||
    state?.account?.org_data?.data?.id ||
    state?.account?.org_member?.org_id
  );

  const accountRole = getUserRole(account);
  const allowedToManage = canManageUsers(account);
  const accountEmail = getAccountEmail(account);
  const [roles, setRoles] = useState([]);
  const [orgAgents, setOrgAgents] = useState([]);
  const [members, setMembers] = useState([]);
  const [selectedTarget, setSelectedTarget] = useState('new');
  const [checkedAgents, setCheckedAgents] = useState([]);
  const [licenseInfo, setLicenseInfo] = useState({
    total_licenses: 0,
    licenses_used: 0,
    licenses_available: 0,
  });
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    if (!orgId || !allowedToManage) return;

    const fetchAdminData = async () => {
      let nextFeedback = null;

      try {
        const licenseRes = await axiosInstance.get(api(`/organization/get_license_info/${orgId}/`));
        setLicenseInfo(licenseRes.data || {});
      } catch (error) {
        console.error('License info error:', error);
        nextFeedback = { type: 'error', message: 'Unable to load license usage.' };
      }

      try {
        const rolesRes = await axiosInstance.get(api('/user/get_roles'));
        setRoles(filterRolesByAccountRole(rolesRes.data || [], accountRole));
      } catch (error) {
        console.error('Roles error:', error);
        setRoles(filterRolesByAccountRole(defaultRoles, accountRole));
      }

      try {
        const agentsRes = await axiosInstance.get(api('/organization/listOrgAgents'), {
          params: { org_id: orgId },
        });
        const normalizedAgents = normalizeOrgAgents(agentsRes.data);
        setOrgAgents(normalizedAgents.length > 0 ? normalizedAgents : normalizeOrgAgents(account.org_agent_seat_pools));
      } catch (error) {
        console.error('Org agents error:', error);
        setOrgAgents(normalizeOrgAgents(account.org_agent_seat_pools));
        nextFeedback = nextFeedback || { type: 'error', message: 'No assignable agents found for this organization.' };
      }

      try {
        const membersRes = await axiosInstance.get(api('/organization/list_members/'), {
          params: { org_id: orgId, created_by_email: accountEmail },
        });
        setMembers(normalizeMembers(membersRes.data));
      } catch (error) {
        console.error('Org members error:', error);
        setMembers([]);
      }

      setFeedback(nextFeedback);
    };

    fetchAdminData();
  }, [account.org_agent_seat_pools, accountEmail, accountRole, allowedToManage, orgId]);

  const selfMember = useMemo(() => getSelfMember(account, accountRole), [account, accountRole]);
  const selectedMember = useMemo(() => {
    if (selectedTarget === 'new') return null;
    if (selectedTarget === 'self') return selfMember;
    return members.find((member) => String(member.id) === selectedTarget) || null;
  }, [members, selectedTarget, selfMember]);
  const selectedMemberAgents = useMemo(() => getAssignedAgents(selectedMember), [selectedMember]);
  const isEditingMember = Boolean(selectedMember);

  const assignableAgents = useMemo(
    () => orgAgents
      .map((agent) => {
        const currentSeats = Number(selectedMemberAgents[agent.code] || 0);
        const availableSeats = Number(agent.totalSeats || 0);

        return {
          ...agent,
          totalSeats: availableSeats + currentSeats,
          currentSeats,
        };
      })
      .filter((agent) => agent.active !== false && Number(agent.totalSeats || 0) > 0),
    [orgAgents, selectedMemberAgents]
  );

  useEffect(() => {
    if (assignableAgents.length === 1 && checkedAgents.length === 0) {
      setCheckedAgents([assignableAgents[0].code]);
    }
  }, [assignableAgents, checkedAgents.length]);

  useEffect(() => {
    const allowedCodes = new Set(assignableAgents.map((agent) => agent.code));
    setCheckedAgents((prev) => prev.filter((code) => allowedCodes.has(code)));
  }, [assignableAgents]);

  useEffect(() => {
    if (!selectedMember) {
      setForm(initialForm);
      setCheckedAgents([]);
      setErrors({});
      return;
    }

    setForm({
      memberName: selectedMember.memberName || '',
      email: selectedMember.email || '',
      password: '',
      phone: selectedMember.phone || '',
      role: normalizeRoleForSubmit(selectedMember.role || accountRole || ''),
    });
    setCheckedAgents(Object.keys(selectedMemberAgents).filter((code) => Number(selectedMemberAgents[code] || 0) > 0));
    setErrors({});
  }, [accountRole, selectedMember, selectedMemberAgents]);

  const licensePercent = useMemo(() => {
    if (!licenseInfo.total_licenses) return 0;
    return Math.min(100, Math.round((licenseInfo.licenses_used / licenseInfo.total_licenses) * 100));
  }, [licenseInfo]);

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const toggleAgent = (code) => {
    setCheckedAgents((prev) =>
      prev.includes(code) ? prev.filter((item) => item !== code) : [...prev, code]
    );
    setErrors((prev) => ({ ...prev, agents: undefined }));
  };

  const handleTargetChange = (value) => {
    setSelectedTarget(value || 'new');
    setFeedback(null);
  };

  const validate = () => {
    const nextErrors = {};
    if (!form.memberName.trim()) nextErrors.memberName = 'User name is required';
    if (form.memberName && !/^[A-Za-z\s]+$/i.test(form.memberName)) {
      nextErrors.memberName = 'User name can contain letters and spaces only';
    }
    if (!form.email.trim()) nextErrors.email = 'Email is required';
    if (form.email && !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(form.email)) {
      nextErrors.email = 'Invalid email address';
    }
    if (!isEditingMember && !form.password) nextErrors.password = 'Password is required';
    if (!form.phone.trim()) nextErrors.phone = 'Phone number is required';
    if (form.phone && !/^[0-9]+$/.test(form.phone)) nextErrors.phone = 'Phone number must be numeric';
    if (!form.role) nextErrors.role = 'Role selection is required';
    if (assignableAgents.length === 0 && !isEditingMember) nextErrors.agents = 'No assignable agents are available. Assign seats from Manage Subscription first.';
    if (assignableAgents.length > 0 && checkedAgents.length === 0 && !isEditingMember) nextErrors.agents = 'Select at least one agent.';
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      setFeedback({ type: 'error', message: 'Please fix the highlighted fields before saving the user.' });
    }
    return Object.keys(nextErrors).length === 0;
  };

  const memberSubmit = async (event) => {
    event.preventDefault();
    setFeedback(null);
    if (!validate()) return;

    setSaving(true);
    try {
      const selectedAllocations = checkedAgents
        .filter(Boolean)
        .reduce((next, agentCode) => ({ ...next, [agentCode]: 1 }), {});

      const payload = {
        ...form,
        password: form.password.trim(),
        role: normalizeRoleForSubmit(form.role),
        org_id: orgId,
        member_id: getNumericMemberId(selectedMember),
        created_by_email: accountEmail,
        allocations: checkedAgents
          .filter(Boolean)
          .map((agentCode) => ({ agent_code: agentCode, seats: 1 })),
        pending_license_allocation: {
          allocations: selectedAllocations,
          created_by_email: accountEmail,
        },
        assigned_agents: selectedAllocations,
      };

      if (isEditingMember && !payload.password) {
        delete payload.password;
      }

      const res = await axiosInstance.post(api('/organization/update_member'), payload);
      setFeedback({ type: 'success', message: res.data?.message || (isEditingMember ? 'User updated successfully.' : 'User created successfully.') });

      try {
        const updatedLicenseInfo = await axiosInstance.get(api(`/organization/get_license_info/${orgId}/`));
        setLicenseInfo(updatedLicenseInfo.data || {});
      } catch (licenseError) {
        console.error('License refresh error:', licenseError);
      }

      try {
        const membersRes = await axiosInstance.get(api('/organization/list_members/'), {
          params: { org_id: orgId, created_by_email: accountEmail },
        });
        setMembers(normalizeMembers(membersRes.data));
      } catch (membersError) {
        console.error('Members refresh error:', membersError);
      }

      setForm(initialForm);
      setCheckedAgents([]);
      setSelectedTarget('new');
      setErrors({});
    } catch (error) {
      setFeedback({ type: 'error', message: getErrorMessage(error) });
    } finally {
      setSaving(false);
    }
  };

  if (!allowedToManage) {
    return (
      <div className="p-10">
        <Card className="p-10 text-center">
          <h2 className="font-display text-2xl font-semibold">Admin access required</h2>
          <p className="mt-2 text-sm text-muted-foreground">Sign in with an authorized account to manage users.</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="pb-12">
      <PageHeader
        eyebrow="Administration"
        title="User Allocation"
        description="Create workspace users, assign roles, and allocate the exact agent licenses available to this organization."
        actions={
          <span className="inline-flex items-center gap-2 rounded-md border border-border/60 bg-card/60 px-3 py-1.5 text-xs text-muted-foreground">
            <ShieldCheck className="h-4 w-4 text-primary" />
            {accountRole || 'Admin'}
          </span>
        }
      />

      <div className="px-6 lg:px-10 py-6 space-y-6">
        <div className="grid gap-4 md:grid-cols-3">
          <LicenseStat label="Total Licenses" value={licenseInfo.total_licenses || 0} />
          <LicenseStat label="Licenses Used" value={licenseInfo.licenses_used || 0} />
          <LicenseStat label="Licenses Available" value={licenseInfo.licenses_available || 0} accent="success" />
        </div>

        <Card className="p-5">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-md bg-primary/10 grid place-items-center">
                <Users className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h2 className="font-display text-lg font-semibold">User Allocation</h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  Add a member or update an existing member's assigned agent licenses.
                </p>
              </div>
            </div>
            <div className="min-w-[180px]">
              <div className="flex items-center justify-between text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
                <span>License usage</span>
                <span>{licensePercent}%</span>
              </div>
              <div className="mt-2 h-2 rounded-full bg-muted overflow-hidden">
                <div className="h-full rounded-full bg-primary" style={{ width: `${licensePercent}%` }} />
              </div>
            </div>
          </div>

          {feedback?.message && (
            <div
              role="alert"
              className={`mt-5 rounded-md border px-3 py-2 text-sm ${
                feedback.type === 'success'
                  ? 'border-success/30 bg-success/10 text-success'
                  : 'border-destructive/30 bg-destructive/10 text-destructive'
              }`}
            >
              {feedback.message}
            </div>
          )}

          <form onSubmit={memberSubmit} className="mt-6 grid gap-5">
            <Field label="Allocation Target">
              <Select value={selectedTarget} onValueChange={handleTargetChange} className="h-9 w-full rounded-md border-input bg-transparent py-1 shadow-sm">
                <SelectItem value="new">Create new user</SelectItem>
                {selfMember?.email && <SelectItem value="self">Assign to myself ({selfMember.email})</SelectItem>}
                {members.map((member) => (
                  <SelectItem key={member.id} value={String(member.id)}>
                    {member.memberName || member.email} {member.email ? `- ${member.email}` : ''}
                  </SelectItem>
                ))}
              </Select>
            </Field>

            <div className="grid gap-5 lg:grid-cols-2">
              <Field label="User Name" error={errors.memberName} required>
                <Input placeholder="Enter user name" value={form.memberName} onChange={(event) => updateField('memberName', event.target.value)} />
              </Field>

              <Field label="User Role" error={errors.role} required>
                <Select value={form.role} onValueChange={(value) => updateField('role', value)} className="h-9 w-full rounded-md border-input bg-transparent py-1 shadow-sm">
                  <SelectItem value="">Select Role</SelectItem>
                  {roles.map((item, index) => (
                    <SelectItem key={`${item.role}-${index}`} value={item.role}>
                      {item.role}
                    </SelectItem>
                  ))}
                </Select>
              </Field>
            </div>

            <div className="grid gap-5 lg:grid-cols-3">
              <Field label="Email" error={errors.email} required>
                <Input type="email" placeholder="Enter email ID" value={form.email} onChange={(event) => updateField('email', event.target.value)} />
              </Field>

              <Field label="Password" error={errors.password} required={!isEditingMember}>
                <Input type="password" placeholder={isEditingMember ? 'Leave blank to keep existing password' : 'Password for user login'} value={form.password} autoComplete="new-password" onChange={(event) => updateField('password', event.target.value)} />
              </Field>

              <Field label="Phone" error={errors.phone} required>
                <Input placeholder="Enter phone" value={form.phone} onChange={(event) => updateField('phone', event.target.value)} />
              </Field>
            </div>

            <div className="rounded-xl border border-border/70 p-4">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-primary" />
                <h3 className="text-sm font-semibold">Agent License Access</h3>
              </div>
              {assignableAgents.length === 0 && !isEditingMember ? (
                <p className="mt-3 text-sm text-muted-foreground">
                  No assignable agents found. Assign seats from Manage Subscription first.
                </p>
              ) : (
                <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                  {assignableAgents.map((agent) => (
                    <label key={agent.code} className="flex cursor-pointer items-start gap-3 rounded-lg border border-border/60 p-3 transition hover:border-primary/30">
                      <input
                        type="checkbox"
                        checked={checkedAgents.includes(agent.code)}
                        onChange={() => toggleAgent(agent.code)}
                        className="mt-1"
                      />
                      <span>
                        <span className="block text-sm font-medium text-foreground">{agent.name}</span>
                        <span className="block text-xs text-muted-foreground">
                          {agent.totalSeats} seats available in organization pool
                          {agent.currentSeats > 0 ? ` (${agent.currentSeats} already assigned to this user)` : ''}
                        </span>
                      </span>
                    </label>
                  ))}
                </div>
              )}
              {errors.agents && assignableAgents.length > 0 && <p className="mt-3 text-xs text-destructive">{errors.agents}</p>}
            </div>

            <div className="flex justify-end border-t border-border/60 pt-5">
              <Button type="submit" disabled={saving || (assignableAgents.length === 0 && !isEditingMember)}>
                <UserPlus className="h-4 w-4 mr-2" />
                {saving ? 'Saving...' : (isEditingMember ? 'Update User' : 'Save Details')}
              </Button>
            </div>
          </form>
        </Card>
      </div>

      {feedback?.message && (
        <div className={`fixed bottom-5 right-5 z-40 flex max-w-sm items-start gap-3 rounded-lg border px-4 py-3 text-sm shadow-lg ${
          feedback.type === 'success'
            ? 'border-emerald-200 bg-white text-emerald-800'
            : 'border-red-200 bg-white text-red-700'
        }`}
        >
          {feedback.type === 'success' ? <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> : null}
          {feedback.type === 'error' ? <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-red-600" /> : null}
          <span>{feedback.message}</span>
        </div>
      )}
    </div>
  );
}

function LicenseStat({ label, value, accent }) {
  const color = accent === 'success' ? 'text-success' : 'text-foreground';
  return (
    <Card className="p-4">
      <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">{label}</p>
      <p className={`mt-1 font-display text-3xl font-semibold ${color}`}>{value}</p>
    </Card>
  );
}

function Field({ label, error, required, children }) {
  return (
    <div className="space-y-2">
      <Label className="text-xs uppercase tracking-[0.14em] text-muted-foreground">
        {label}{required && <span className="text-destructive"> *</span>}
      </Label>
      {children}
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}

function filterRolesByAccountRole(roleRows, accountRole) {
  const normalizedRows = normalizeRoles(roleRows);

  if (accountRole === 'Owner') {
    return normalizedRows.filter((item) => item.role !== 'Owner');
  }

  if (accountRole === 'Super Admin') {
    return normalizedRows.filter((item) => item.role !== 'Owner');
  }

  if (accountRole === 'Admin') {
    return normalizedRows.filter((item) => item.role !== 'Super Admin' && item.role !== 'Owner');
  }

  if (accountRole === 'Department Head') {
    return normalizedRows.filter(
      (item) =>
        item.role !== 'Super Admin' &&
        item.role !== 'Admin' &&
        item.role !== 'Department Head' &&
        item.role !== 'Owner'
    );
  }

  return [];
}

function normalizeRoles(roleRows) {
  const rows = Array.isArray(roleRows) ? roleRows : roleRows?.data || roleRows?.roles || [];
  return rows
    .map((item) => {
      const role = normalizeRoleForSubmit(item.role || item.name || item.role_name || item);
      return role ? { ...item, role } : null;
    })
    .filter(Boolean)
    .filter((item, index, all) => all.findIndex((row) => row.role === item.role) === index);
}

function normalizeRoleForSubmit(role) {
  if (role === 'Staff Member') return 'Member';
  if (role === 'Department Head') return 'Member';
  return typeof role === 'string' ? role : '';
}

function getErrorMessage(error) {
  const data = error?.response?.data;
  if (typeof data === 'string') return data;
  if (data?.message) return data.message;
  if (data?.msg) return data.msg;
  if (data?.error) return data.error;

  if (data && typeof data === 'object') {
    const fieldMessage = Object.entries(data)
      .map(([field, value]) => {
        const message = Array.isArray(value) ? value.join(', ') : value;
        return typeof message === 'string' ? `${field}: ${message}` : null;
      })
      .filter(Boolean)
      .join(' ');

    if (fieldMessage) return fieldMessage;
  }

  return error?.message || 'Unable to create user.';
}

function normalizeOrgAgents(data) {
  if (data && !Array.isArray(data) && typeof data === 'object' && !data.agents && !data.data && !data.results) {
    return Object.entries(data).map(([code, seats]) => ({
      code,
      name: formatAgentName(code),
      totalSeats: Number(seats) || 0,
      active: true,
    })).filter((item) => item.code);
  }

  const rows = Array.isArray(data) ? data : data?.agents || data?.data || data?.results || data?.org_agent_seat_pools || [];
  if (!Array.isArray(rows)) return [];

  return rows.map((item) => {
    const code = item.agent_code || item.agentCode || item.code || item.agent?.code || item.agent?.agent_code || '';
    const totalSeats =
      item.available_seats ??
      item.availableSeats ??
      item.remaining_seats ??
      item.remainingSeats ??
      item.seats_available ??
      item.seatsAvailable ??
      item.total_seats ??
      item.totalSeats ??
      item.allocated_seats ??
      item.seats ??
      item.license_count ??
      item.count ??
      0;

    return {
      code,
      name: item.agent_name || item.agentName || item.name || item.agent?.name || formatAgentName(code),
      totalSeats,
      active: item.is_active ?? item.active ?? item.agent?.is_active ?? true,
    };
  }).filter((item) => item.code);
}

function normalizeMembers(data) {
  const rows = Array.isArray(data) ? data : data?.members || data?.data || data?.results || [];
  if (!Array.isArray(rows)) return [];

  return rows
    .map((item) => ({
      id: item.id || item.member_id || item.memberId || item.userId || item.email,
      memberName: item.memberName || item.member_name || item.name || item.first_name || '',
      email: item.email || '',
      phone: item.phone || item.phone_number || '',
      role: normalizeRoleForSubmit(item.role || item.role_name || ''),
      assigned_agents: getAssignedAgents(item),
    }))
    .filter((item) => item.id && item.email);
}

function getSelfMember(account, accountRole) {
  const orgMember = account?.org_member?.data || account?.org_member || {};
  const user = account?.user?.data || account?.user || account?.UserDetails || account?.UserDetails?.data || {};
  const email = orgMember.email || user.email || account?.email || '';
  if (!email) return null;
  const memberAgents = getAssignedAgents(orgMember);

  return {
    id: orgMember.id || orgMember.member_id || 'self',
    memberName: orgMember.memberName || orgMember.member_name || user.first_name || account?.first_name || '',
    email,
    phone: orgMember.phone || user.phone_number || account?.phone || '',
    role: normalizeRoleForSubmit(accountRole || orgMember.role || user.role || 'Super Admin'),
    assigned_agents: memberAgents,
  };
}

function getAccountEmail(account) {
  const orgMember = account?.org_member?.data || account?.org_member || {};
  const user = account?.user?.data || account?.user || account?.UserDetails || account?.UserDetails?.data || {};
  return orgMember.email || user.email || account?.email || '';
}

function getAssignedAgents(source) {
  if (!source) return {};
  const pending = source.pending_license_allocation?.allocations;
  const assigned = source.assigned_agents || source.assignedAgents || pending || {};
  if (!assigned || typeof assigned !== 'object' || Array.isArray(assigned)) return {};

  return Object.entries(assigned).reduce((next, [code, seats]) => {
    const cleanCode = String(code || '').trim();
    const count = Number(seats || 0);
    return cleanCode && count > 0 ? { ...next, [cleanCode]: count } : next;
  }, {});
}

function getNumericMemberId(member) {
  const numericId = Number(member?.id);
  return Number.isFinite(numericId) && numericId > 0 ? numericId : undefined;
}

function formatAgentName(code) {
  return String(code || '')
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}
